import { BotIcon, CheckIcon } from 'lucide-react';

import { useAgentConnection } from './agent-connection-context';
import { entryLabel } from './agent-connection-model';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

// The collapsed rail keeps the icon and folds the visible label into an
// sr-only span, so each form keeps one accessible name in both rail widths.
const COLLAPSED_ICON_BUTTON =
  'group-data-[collapsible=icon]:size-11! group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:p-0!';
const COLLAPSED_LABEL = 'truncate group-data-[collapsible=icon]:sr-only';

/** A bot with a small completed mark on its corner. */
function ConnectedMark({ arriving }: { readonly arriving: boolean }) {
  return (
    <span aria-hidden="true" className="relative inline-flex shrink-0">
      <BotIcon className="size-4 text-muted-foreground" />
      <span
        className={cn(
          'absolute -right-1 -bottom-1 flex size-3 items-center justify-center rounded-full bg-status-success text-background ring-2 ring-sidebar',
          arriving && 'animate-ca-delight-check [animation-delay:180ms]',
        )}
      >
        <CheckIcon className="size-2" strokeWidth={4} />
      </span>
    </span>
  );
}

/**
 * Sidebar footer entry. Before the first CLI sign-in it is an invitation with
 * one obvious action; afterwards it shrinks to a quiet completed row.
 */
export function SidebarAgentConnectionButton() {
  const connection = useAgentConnection();
  if (connection === null) {
    return null;
  }
  const label = entryLabel(connection.entryStatus);
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        {connection.entryStatus === 'connected' ? (
          <SidebarMenuButton
            className={cn(
              'text-muted-foreground hover:text-foreground',
              COLLAPSED_ICON_BUTTON,
              connection.celebrate && 'animate-ca-enter-down-4',
            )}
            data-agent-connection="connected"
            onClick={connection.openPrompt}
            size="sm"
            tooltip={label}
            type="button"
          >
            <ConnectedMark arriving={connection.celebrate} />
            <span className={COLLAPSED_LABEL}>{label}</span>
          </SidebarMenuButton>
        ) : (
          <SidebarMenuButton
            className={cn('font-medium', COLLAPSED_ICON_BUTTON)}
            data-agent-connection="invitation"
            onClick={connection.openPrompt}
            tooltip={label}
            type="button"
            variant="outline"
          >
            <BotIcon />
            <span className={COLLAPSED_LABEL}>{label}</span>
          </SidebarMenuButton>
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function AgentConnectionMenuItem() {
  const connection = useAgentConnection();
  if (connection === null) {
    return null;
  }
  return (
    <DropdownMenuItem onClick={connection.openPrompt}>
      <BotIcon data-icon="inline-start" />
      {entryLabel(connection.entryStatus)}
    </DropdownMenuItem>
  );
}
